import {customElement, property, query} from 'lit/decorators.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '#lit/types/classes.js';
import type {Model, GroupRegistryItem} from './architecture.js';
import {Msg, main} from './architecture.js';
import {Point} from '#lit/lib/point.js';
import type CanvasConnection from '../canvas_connection/index.js';
import CanvasElement from '../canvas_element/index.js';
import type CanvasNode from '../canvas_node/index.js';
import type {BoundingBox} from '#lit/lib/bezier.js';
import {CanvasWalker} from '#lit/lib/canvas_walker.js';
import {v4} from 'uuid';
import styles from './style.scss';
import {enforceIntoRange} from '#lit/utils/numbers.js';
import {isCanvasGroupType} from '#lit/types/canvas.js';

export const CANVAS_GROUP_ELEMENT_TAG = 'fly-canvas-group';

export const GROUP_INNER_BORDER = 2;
export const GROUP_MARGIN = 24;
export const GROUP_NODE_GAP = 12;
export const GROUP_NODE_WIDTH = 236;
const NODE_TRACKER_WIDTH = 12;
export const MAX_SUBNODES = 9;

/**
 * A component that allows for management of grouped canvas nodes.
 */
@customElement(CANVAS_GROUP_ELEMENT_TAG)
export default class CanvasGroup extends ImmutableComponent<
  Constructor<CanvasElement>,
  Model,
  Msg
>({component: CanvasElement, main}) {
  private _canvasType = 'group';
  @property({type: Array}) nodes: CanvasNode[] = [];

  @query('.canvas-group__header') $header?: HTMLElement;
  @query('.canvas-group') $node?: HTMLElement;

  walker: CanvasWalker = new CanvasWalker(this);

  static get styles() {
    return [styles];
  }

  static getGroupFromPath(composedPath: EventTarget[]): CanvasGroup | null {
    const node = composedPath.find(el => isCanvasGroupType(el)) as
      | CanvasGroup
      | undefined;

    // FIXME(joshua): Make this logic clearer.
    if (node) {
      const header = composedPath.find(
        el =>
          el instanceof HTMLElement &&
          el.classList.contains('canvas-group__header')
      );

      return header ? node : null;
    }

    return null;
  }

  // FIXME(joshua): Create + use utility for this in all canvas element classes.
  static isInPath(composedPath: EventTarget[]): boolean {
    const node = composedPath.find(el => isCanvasGroupType(el)) as
      | CanvasGroup
      | undefined;

    return !!node;
  }

  protected getNodeBoundingBox(index: number) {
    return Point.from(GROUP_NODE_WIDTH * index + GROUP_NODE_GAP * index, 0);
  }

  isInPath(path: EventTarget[]) {
    if (!this.$header) return false;

    return !!path.find(el => {
      return el instanceof Element && el.isSameNode(this.$header!);
    });
  }

  getNodePosition(key: string): Point | null {
    if (!this.model.registry.has(key)) return null;

    const item = this.model.registry.get(key);
    if (!item) return null;
    const index = Math.max(0, item.order - 1);
    const boundingBox = this.getNodeBoundingBox(index);

    return Point.fromTuple(this.model.position.asTuple())
      .add(Point.from(GROUP_MARGIN))
      .add(Point.from(0, this.$header?.clientHeight))
      .add(boundingBox);
  }

  createRegistryItem<Metadata>(
    metadata: Metadata
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): GroupRegistryItem<any, any> {
    return {
      key: v4(),
      position: this.model.position,
      order: this.model.registry.size,
      metadata,
    };
  }

  onNewNodeClick() {
    if (this.model.registry.size >= MAX_SUBNODES) return;
    const item = this.createRegistryItem(null);
    this.handleUpdate(Msg.addNode(item));
  }

  calcSocketPosition(): number {
    if (!this.$canvas || !this.$node) return 0;
    const socket = this.socketOffset();
    const nodeOffset = this.offsetLeft ?? 0;
    const spacingOffset = GROUP_INNER_BORDER;
    const trackerWidth = NODE_TRACKER_WIDTH;

    return socket.x - nodeOffset - spacingOffset - trackerWidth / 2;
  }

  // FIXME(joshua):
  // Create a 3rd classes for composition to share this logic with CanvasNode
  connect(connection: CanvasConnection) {
    this.walker.clear();

    this.handleUpdate(Msg.addInput(connection));

    connection.addEventListener('destroyed', () => {
      this.disconnect(connection);
    });
  }

  connectorBoundingBox({index}: {index: number}) {
    const wrapper = this.shadowRoot?.querySelector(
      `#connector-wrapper-${index}`
    );

    if (!wrapper || !(wrapper instanceof HTMLElement)) return;

    const baseOffset = this.offsetLeft;
    const groupOffset = Point.from(0);

    const x =
      typeof baseOffset === 'number'
        ? baseOffset + groupOffset.x + wrapper.clientWidth * index
        : 0 + groupOffset.x;

    return {
      x,
      y: (this.$node?.offsetTop ?? 0) + groupOffset.y,
      width: wrapper.clientWidth ?? 0,
      height: this.$node?.clientHeight ?? 0,
    };
  }

  // FIXME(joshua):
  // Create a 3rd classes for composition to share this logic with CanvasNode
  disconnect(connection: CanvasConnection) {
    this.handleUpdate(Msg.removeInput(connection));
    this.walker.clear();
  }

  // FIXME(joshua):
  // Create a 3rd classes for composition to share this logic with CanvasNode
  setSocketPosition() {
    this.style.setProperty(
      '--tracker-position',
      `${this.calcSocketPosition()}px`
    );

    this.style.setProperty(
      '--tracker-display',
      this.calcSocketPosition() === 0 ? 'none' : 'block'
    );
  }

  get canvasType() {
    return this._canvasType;
  }

  // FIXME(joshua):
  // Create a 3rd classes for composition to share this logic with CanvasNode
  get boundingBox(): BoundingBox {
    const groupNode = this.$node;
    return {
      x: this.offsetLeft ?? 0,
      y: this.offsetTop ?? 0,
      width: groupNode?.clientWidth ?? 0,
      height: groupNode?.clientHeight ?? 0,
    };
  }

  socketOffset(): Point {
    if (!this.$canvas) return Point.from(0, 0);

    const container = this.$node;

    const rect = container?.getBoundingClientRect() ?? {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    };

    const mouseX = this.$canvas?.cursor.globalPosition.x ?? 0;
    const planeRect = this.$canvas?.model.planeRect ?? {x: 0, y: 0};
    const scale = this.$canvas?.model.scale ?? 1;

    const posX = enforceIntoRange(rect.left, mouseX, rect.right);
    const posY = rect.top;

    const x = (posX - planeRect.x) / scale;
    const y = (posY - planeRect.y) / scale;

    return Point.from(x, y);
  }

  removeNode(ref: HTMLElement) {
    const item = Array.from(this.model.registry.values()).find(item => {
      return item.ref === ref;
    });
    if (item) this.handleUpdate(Msg.removeNode(item.key));
  }

  delete() {
    if (this.model.inputs.size) {
      this.model.inputs.forEach(input => {
        (input as CanvasConnection).delete();
      });
    }

    super.delete();
  }

  root() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CANVAS_GROUP_ELEMENT_TAG]: CanvasGroup;
  }
}
